import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { tournamentService, athleteService, userService } from '../../services/firebaseService';

const SimpleDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Récupérer le profil utilisateur
        const profileData = await userService.getUserProfile();
        setUserProfile(profileData.user);
        
        // Récupérer les tournois
        const tournamentsData = await tournamentService.getAllTournaments();
        setTournaments(tournamentsData);
        
        // Récupérer les athlètes
        const athletesData = await athleteService.getAllAthletes();
        setAthletes(athletesData);
        
        // Filtrer les tournois récents (créés dans les 30 derniers jours)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recent = tournamentsData
          .filter(tournament => new Date(tournament.createdAt) > thirtyDaysAgo)
          .slice(0, 3);
        setRecentTournaments(recent);
        
        // Filtrer les tournois à venir
        const today = new Date();
        const upcoming = tournamentsData
          .filter(tournament => new Date(tournament.date) > today)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setUpcomingTournaments(upcoming);
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des données du tableau de bord:', err);
        setError('Impossible de charger les données du tableau de bord. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container className="my-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <h2 className="mb-4">Tableau de bord</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {/* Statistiques */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">{tournaments.length}</h1>
              <Card.Title>Tournois</Card.Title>
              <Link to="/tournaments" className="btn btn-outline-primary mt-2">
                Voir tous les tournois
              </Link>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">{athletes.length}</h1>
              <Card.Title>Athlètes</Card.Title>
              <Link to="/athletes" className="btn btn-outline-primary mt-2">
                Voir tous les athlètes
              </Link>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">{userProfile?.subscriptionType || 'Free'}</h1>
              <Card.Title>Abonnement</Card.Title>
              <Link to="/profile" className="btn btn-outline-primary mt-2">
                Gérer mon abonnement
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Tournois récents */}
      <h3 className="mb-3">Tournois récents</h3>
      {recentTournaments.length === 0 ? (
        <Alert variant="info">
          Vous n'avez pas créé de tournois récemment.
        </Alert>
      ) : (
        <Row className="mb-4">
          {recentTournaments.map(tournament => (
            <Col md={4} key={tournament.id} className="mb-3">
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>{tournament.name}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {tournament.location} - {new Date(tournament.date).toLocaleDateString()}
                  </Card.Subtitle>
                  <Card.Text>
                    <span className={`badge bg-${getStatusBadgeColor(tournament.status)}`}>
                      {getStatusLabel(tournament.status)}
                    </span>
                    <br />
                    {tournament.athletes ? tournament.athletes.length : 0} athlètes
                  </Card.Text>
                  <Link to={`/tournaments/${tournament.id}`} className="btn btn-primary">
                    Gérer
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Tournois à venir */}
      <h3 className="mb-3">Tournois à venir</h3>
      {upcomingTournaments.length === 0 ? (
        <Alert variant="info">
          Vous n'avez pas de tournois à venir.
        </Alert>
      ) : (
        <Row className="mb-4">
          {upcomingTournaments.map(tournament => (
            <Col md={4} key={tournament.id} className="mb-3">
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>{tournament.name}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {tournament.location} - {new Date(tournament.date).toLocaleDateString()}
                  </Card.Subtitle>
                  <Card.Text>
                    <span className={`badge bg-${getStatusBadgeColor(tournament.status)}`}>
                      {getStatusLabel(tournament.status)}
                    </span>
                    <br />
                    {tournament.athletes ? tournament.athletes.length : 0} athlètes
                  </Card.Text>
                  <Link to={`/tournaments/${tournament.id}`} className="btn btn-primary">
                    Gérer
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Actions rapides */}
      <h3 className="mb-3">Actions rapides</h3>
      <Row>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <Card.Title>Créer un tournoi</Card.Title>
              <Card.Text>
                Créez un nouveau tournoi et commencez à y ajouter des athlètes.
              </Card.Text>
              <Link to="/tournaments" className="btn btn-primary">
                Créer un tournoi
              </Link>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <Card.Title>Ajouter des athlètes</Card.Title>
              <Card.Text>
                Ajoutez de nouveaux athlètes à votre base de données.
              </Card.Text>
              <Link to="/athletes" className="btn btn-primary">
                Ajouter des athlètes
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

// Fonctions utilitaires
const getStatusLabel = (status) => {
  switch (status) {
    case 'draft': return 'Brouillon';
    case 'in_progress': return 'En cours';
    case 'completed': return 'Terminé';
    case 'cancelled': return 'Annulé';
    default: return 'Inconnu';
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'draft': return 'secondary';
    case 'in_progress': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'danger';
    default: return 'info';
  }
};

export default SimpleDashboard;
